import React from "react";

import ImageCanvasCommonNew from "../visComponents/imageCanvasCommonNew";
import RzppPinchAdapter from "./rzppPinchAdapter";
import { setVisualizerInstance } from "../docking/dockStore";
import Misc from "../../misc/misc";
import { isTouchDevice } from "../../misc/utils";

// A pointer move larger than this (in px) between mousedown and mouseup is
// treated as a pan/drag, not a click, so tiny jitter still opens the menu.
// Exported so DockedVisualizerWrapper's rzpp onPanning fallback uses the same
// threshold instead of a second, potentially-drifting magic number.
export const DRAG_THRESHOLD_PX = 5;

/**
 * "New shell, old brain."
 *
 * Extends ImageCanvasCommonNew so ALL of the existing draw / hover / green-zone /
 * clickability logic (which layers we can click, which we highlight) is reused
 * verbatim. It renders ONLY the <canvas> (no zoom/pan of its own) — pan/zoom and
 * the scale-to-parent transform are owned by the persistent <DockedVisualizerWrapper>,
 * which wraps this canvas in react-zoom-pan-pinch's <TransformComponent>.
 *
 * This component is mounted exactly ONCE (inside VisualizerDockShell) and never
 * unmounted on route changes — only its props (layers / canvasId / display size)
 * change. The clickable <canvas> keeps id={canvasId}, so navigation.bindCanvasClick
 * still attaches to document.getElementById(canvasId).
 *
 * Props (in addition to the usual layers/canvasId/layout/width/height/useMouseHovering):
 *   - getRzpp: () => ReactZoomPanPinchRef | null  — lets the pinch shim read the live scale
 *   - canvasDisplay: { width, height }            — CSS display size (the transform content size)
 */
export default class ImageCanvasVisualizer extends ImageCanvasCommonNew {
  // Ad-hoc instance fields owned by this subclass. Given plain (non-`declare`) initial
  // values rather than `declare`d — this project's babel.config.js runs
  // @babel/plugin-proposal-class-properties before @babel/preset-typescript strips types
  // (plugins always run before presets), and that plugin doesn't understand the `declare`
  // modifier without the `allowDeclareFields` option, which isn't set here. Each initial
  // value below matches the implicit `undefined` these fields already started as in the
  // original untyped JS, so this changes no runtime behavior.
  canvas: HTMLCanvasElement | null = null;
  pinchZoom: any;
  pointerDown = false;
  isDragging = false;
  _downX = 0;
  _downY = 0;

  componentDidMount() {
    console.log("VisualizerAdapter Mounted", );
    super.componentDidMount();
    // Always create the rzpp-backed pinch shim (desktop + mobile).
    this.createPinchZoom();
    setVisualizerInstance(this);
    this.announceCanvasReady();
  }

  // The redraw path above (finishDraw, called from draw()) is the usual way
  // setMouseHoverFunction gets invoked, but draw() only runs when the LAYERS prop
  // actually changes (see the base class's UNSAFE_componentWillReceiveProps -
  // it diffs nextProps.layers against state.layers and skips redraw otherwise).
  // Under the persistent docked canvas (USE_NEW_CANVAS), useMouseHovering flips
  // true/false purely from a dock-mode/slot switch (e.g. embedded /materials ->
  // fullscreen /visualizer - see useDockedVisualizerInput.ts) with the SAME layers
  // already on screen, so that redraw never happens and the mousemove listener
  // never gets attached - hovering looks completely dead after the very first such
  // switch. Catch that transition directly instead of relying on a redraw.
  // update - added isTouch to reassign when we go mobile -> desktop
  componentDidUpdate(prevProps) {
    if (!this.state.mouseMoveSet && (this.props.useMouseHovering && !prevProps.useMouseHovering)) {
      this.drawPropsLayers(); // set state layers and attach mouse move
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    setVisualizerInstance(null);
  }

  announceCanvasReady = () => {
    const props: any = this.props;
    if (props.canvasId) {
      window.dispatchEvent(
        new CustomEvent("dockCanvasReady", { detail: { canvasId: props.canvasId } })
      );
    }
  };

  // Override: no lib/pinch-zoom. Provide the rzpp-backed shim, reading the live
  // transform ref supplied by the wrapper via props.getRzpp.
  createPinchZoom = () => {
    const props: any = this.props;
    if (!this.pinchZoom) {
      this.pinchZoom = new RzppPinchAdapter(() => (props.getRzpp ? props.getRzpp() : null));
    }
  };

  /**
   * Override: the legacy "scroll the canvas to the clicked zone" hook.
   *
   * ImageCanvasCommon.imageCanvasScroll() does two things: `this.pinchZoom.reset()`
   * and then `this.pinchZoom.imageCanvasScroll(...)` (the hard-coded per-zone
   * offsets in lib/pinchScroll.js). It is called from visualizer.js's
   * setCanvasClass(), which every mobile navigation calls on essentially every
   * interaction — opening the menu, clicking a parent category, clicking a sub
   * part, clicking a product, and closing the menu (`setCanvasClass('none', true)`).
   *
   * Under the docked canvas the SECOND half is already a no-op (RzppPinchAdapter
   * .imageCanvasScroll), but the FIRST half was not: RzppPinchAdapter.reset() calls
   * `resetTransform(300)` on the live react-zoom-pan-pinch ref, which throws away
   * whatever the user had panned to and animates back to the initial centered
   * transform. On a fullscreen mobile/tablet layout (e.g. iPad Air 768x1024, where
   * the canvas is taller than wide and only pans horizontally) that showed up as
   * "the canvas jumps all the way back to the left as soon as I tap a navigation
   * item". The old guard `!window.canvasIsDragging` never helped here either —
   * that flag was only ever set by lib/pinch-zoom, which the docked canvas
   * doesn't use.
   *
   * So: no-op. Nothing should move the docked canvas implicitly. The only
   * deliberate, framed repositioning is useZoomToActiveFeatureGroup (driven by the
   * active feature group's partId, not by a nav click), and manual panning stays
   * entirely the user's.
   */
  imageCanvasScroll = () => {};

  // Cursor: show a grab/drag cursor while panning, and let the inherited hover
  // logic (setHoveredLayer) drive the pointer cursor otherwise. The parent
  // handlers are arrow-function class fields (not on the prototype), so their
  // pinchZoom delegation is re-implemented here rather than called via super.
  onMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    this.pointerDown = true;
    this.isDragging = false;
    this._downX = event.clientX;
    this._downY = event.clientY;
    // Clear any stale flag from a previous gesture that never produced a click.
    (window as any).canvasWasDragged = false;
    if (this.pinchZoom && this.pinchZoom.onMouseDown) {
      this.pinchZoom.onMouseDown();
    }
  };

  // onMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
  onMouseMove = () => {
    if (this.pointerDown && this.canvas) {
      this.isDragging = true;
      this.canvas.style.cursor = "grabbing"; // dragging / panning the canvas
    }
    if (this.pinchZoom && this.pinchZoom.onMouseMove) {
      this.pinchZoom.onMouseMove();
    }
  };

  onMouseUp = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const props: any = this.props;
    // If the pointer travelled past the threshold, mark this gesture as a drag so
    // the click the browser fires next is swallowed by navigation's
    // _handleCanvasClick instead of opening the menu at the drop point.
    if (this.pointerDown) {
      const dx = event.clientX - (this._downX || 0);
      const dy = event.clientY - (this._downY || 0);
      if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
        (window as any).canvasWasDragged = true;
      }
    }
    this.resetDragState();
    if (this.pinchZoom && this.pinchZoom.onMouseUp) {
      this.pinchZoom.onMouseUp(event, props.lookCanvasClick);
    } else if (props.lookCanvasClick) {
      props.lookCanvasClick();
    }
  };

  // Clears the same pointerDown/isDragging/cursor bookkeeping onMouseUp normally
  // clears, but callable from outside (DockedVisualizerWrapper) too. This canvas's
  // own onMouseUp is a React handler bound within the canvas subtree, so it only
  // fires when the mouseup ending a drag lands back on the canvas itself. On the
  // small embedded /materials dock it's easy for a pan to end with the pointer
  // released elsewhere (e.g. right on the "open fullscreen visualizer" button) -
  // in that case onMouseUp never runs, pointerDown/isDragging stay stuck true, and
  // every subsequent mousemove keeps forcing the cursor back to "grabbing" (see
  // onMouseMove below), which then also masks the hover-pointer cursor once we've
  // switched to fullscreen - looking like hovering stopped working entirely.
  // DockedVisualizerWrapper calls this on every dock-mode switch as a fallback,
  // mirroring the existing rzpp onPanning belt-and-suspenders fix for
  // window.canvasWasDragged (see its own comment for that half of the story).
  //
  // (The separate "useMouseHovering turned on but the mousemove listener never got
  // attached" issue - also hit on this same embedded->fullscreen switch - is fixed
  // at its own source in ImageCanvasCommonNew.componentDidUpdate, not here.)
  resetDragState = () => {
    this.pointerDown = false;
    this.isDragging = false;
    if (this.canvas) {
      this.canvas.style.cursor = ""; // release: hover logic takes over again
    }
  };

  render() {
    const props: any = this.props;
    const canvasId = props.canvasId;
    const isMobile = Misc.isMobile() || isTouchDevice();
    const display = props.canvasDisplay || {};

    return (
      <div className="canvas canvas--dock">
        <canvas
          ref={(el) => { this.canvas = el; }}
          className={"canvas-el" + (props.useMouseHovering ? " canvas-el--pointer" : "")}
          onMouseDown={this.onMouseDown}
          onMouseMove={this.onMouseMove}
          onMouseUp={this.onMouseUp}
          id={canvasId}
          width={props.width}
          height={props.height}
          data-mobile={isMobile}
          style={{
            display: "block",
            width: display.width ? `${display.width}px` : "100%",
            height: display.height ? `${display.height}px` : "100%",
            touchAction: "none",
          }}
        />
      </div>
    );
  }
}
